/**
 * From-stock cards (104.1, D-2, D-4): how form lines group into cards, and a
 * colour's identity. Pure, types only, so the reducer, the payload builders
 * and the rail checks share ONE rule.
 *
 * A card is the run of consecutive stock lines sharing one `groupId`, one
 * line per ticked colour; a customer supplied or free form line is a card of
 * its own.
 *
 * Colour identity (B4/F1): a colour is keyed by its `colourCode`, the LAST
 * segment of its SKUs (the server's `colour_seg`), never by its name: two
 * colours may share a name, or have none. A stock line's `colour` holds that
 * code; the name the floor reads comes from the garment's colour.
 */
import type {
  ManualCatalogColour,
  ManualLineState,
  ManualPrintMethod,
  NonStockLineState,
  StockLineProduct,
  StockLineState,
} from '../types';

// ---------------------------------------------------------------------------
// Colour identity
// ---------------------------------------------------------------------------

/** A colour's identity: its colour code (the last SKU segment). */
export function colourKey(colour: Pick<ManualCatalogColour, 'colourCode'>): string {
  return colour.colourCode;
}

/** The colour code of `sku`: its LAST segment ('' for no SKU). */
export function skuColourCode(sku: string | null | undefined): string {
  const segments = (sku ?? '').split('-');
  return segments[segments.length - 1] ?? '';
}

/** The colour code of saved sizes: the first SKU's last segment, or null when no size carries a SKU. */
export function sizesColourCode(sizes: ReadonlyArray<{ sku?: string | null }>): string | null {
  const sku = sizes.find((size) => !!size.sku)?.sku;
  return sku ? skuColourCode(sku) : null;
}

/**
 * The garment's colour for a saved colour: by code when the saved sizes
 * carry a SKU, else by name only when exactly one colour has that name.
 */
export function findColour(
  product: Pick<StockLineProduct, 'colours'>,
  code: string | null,
  name: string,
): ManualCatalogColour | undefined {
  if (code !== null) return product.colours.find((colour) => colour.colourCode === code);
  const named = product.colours.filter((colour) => (colour.color ?? '') === name);
  return named.length === 1 ? named[0] : undefined;
}

/** The colour of a stock line on its garment, or undefined when none is ticked. */
export function lineColour(line: StockLineState): ManualCatalogColour | undefined {
  if (line.colour === null || !line.product) return undefined;
  return line.product.colours.find((colour) => colour.colourCode === line.colour);
}

/** The colour's name as the floor reads it ('' for a colourless product or no colour). */
export function colourNameOf(line: StockLineState): string {
  return lineColour(line)?.color ?? '';
}

// ---------------------------------------------------------------------------
// Print methods
// ---------------------------------------------------------------------------

/** Methods in every list, in the first list's order (default first); [] for no lists. */
export function intersectMethods(lists: ReadonlyArray<readonly string[]>): ManualPrintMethod[] {
  const [first, ...rest] = lists;
  if (!first) return [];
  return (first as ManualPrintMethod[]).filter((method) => rest.every((list) => list.includes(method)));
}

/** Methods every colour of the card can take, in the lead's order (default first). */
export function cardMethods(lines: readonly ManualLineState[]): ManualPrintMethod[] {
  return intersectMethods(lines.map((line) => line.printMethods));
}

// ---------------------------------------------------------------------------
// Grouping
// ---------------------------------------------------------------------------

export function inCard(line: ManualLineState | undefined, groupId: string): line is StockLineState {
  return line?.kind === 'stock' && line.groupId === groupId;
}

/** `[start, end)` of the card holding `lines[index]` (a non-stock line is a card of its own). */
export function cardRange(lines: readonly ManualLineState[], index: number): [number, number] {
  const line = lines[index];
  if (!line || line.kind !== 'stock') return [index, index + 1];
  let start = index;
  while (start > 0 && inCard(lines[start - 1], line.groupId)) start -= 1;
  let end = index + 1;
  while (end < lines.length && inCard(lines[end], line.groupId)) end += 1;
  return [start, end];
}

/** The lines of the card holding `lineId`, lead first ([] when the line is gone). */
export function cardOf(lines: readonly ManualLineState[], lineId: string): ManualLineState[] {
  const index = lines.findIndex((line) => line.id === lineId);
  if (index === -1) return [];
  const [start, end] = cardRange(lines, index);
  return lines.slice(start, end);
}

/** The from-stock card `groupId` names (its first run), lead first. */
export function cardLines(lines: readonly ManualLineState[], groupId: string): StockLineState[] {
  const index = lines.findIndex((line) => inCard(line, groupId));
  if (index === -1) return [];
  const [start, end] = cardRange(lines, index);
  return lines.slice(start, end) as StockLineState[];
}

/** A card as the Garments and print card renders it. */
export type ManualLineCard =
  | { kind: 'stock'; key: string; groupId: string; lines: StockLineState[]; start: number }
  | { kind: 'nonStock'; key: string; line: NonStockLineState; start: number };

/** Every card in order: one per from-stock group, one per customer supplied or free form line. */
export function groupLineCards(lines: readonly ManualLineState[]): ManualLineCard[] {
  const cards: ManualLineCard[] = [];
  const seen = new Set<string>();
  let index = 0;
  while (index < lines.length) {
    const line = lines[index]!;
    if (line.kind !== 'stock') {
      cards.push({ kind: 'nonStock', key: line.id, line, start: index });
      index += 1;
      continue;
    }
    const [, end] = cardRange(lines, index);
    // A group id seen before (two runs, only from a draft built elsewhere) keys by position.
    const key = seen.has(line.groupId) ? `${line.groupId}:${index}` : line.groupId;
    seen.add(line.groupId);
    cards.push({ kind: 'stock', key, groupId: line.groupId, lines: lines.slice(index, end) as StockLineState[], start: index });
    index = end;
  }
  return cards;
}

/** The lines of one card: a stock card's colour lines, else the one line. */
export function linesOfCard(card: ManualLineCard): ManualLineState[] {
  return card.kind === 'stock' ? card.lines : [card.line];
}

// ---------------------------------------------------------------------------
// Regrouping saved lines (edit.get rows, draft lines) into cards
// ---------------------------------------------------------------------------

export interface RegroupRule<T> {
  /** The item's own line id. */
  id: (item: T) => string;
  /** Its stored group id, or null (not from stock). */
  group: (item: T) => string | null;
  /** May `lead` start a run of more than one colour (from stock, grouped, a garment code, a colour)? */
  canLead: (lead: T) => boolean;
  /** May `next` join the run `lead` starts, besides sharing its group and bringing a new colour? */
  joins: (lead: T, next: T) => boolean;
  /** Its colour identity (the colour code), or null for none, which ends a run. */
  colour: (item: T) => string | null;
}

/**
 * The ONE regroup rule for saved lines (edit view and draft restore, D-6):
 * a card is a run of consecutive items with the lead's group id, each
 * joining by `rule.joins` and bringing a colour the run does not hold yet;
 * the first item that breaks it starts the next run. A run is keyed by its
 * stored group id the first time that id is met, else by its first item's
 * own id (a later run of one group id becomes a card of its own).
 */
export function regroupRuns<T>(items: readonly T[], rule: RegroupRule<T>): Array<{ groupId: string; items: T[] }> {
  const runs: Array<{ groupId: string; items: T[] }> = [];
  const used = new Set<string>();
  let index = 0;
  while (index < items.length) {
    const lead = items[index]!;
    const run = [lead];
    index += 1;
    const group = rule.group(lead);
    if (group !== null && rule.canLead(lead)) {
      const colours = new Set([rule.colour(lead)]);
      while (index < items.length) {
        const next = items[index]!;
        const colour = rule.colour(next);
        if (rule.group(next) !== group || colour === null || colours.has(colour) || !rule.joins(lead, next)) break;
        run.push(next);
        colours.add(colour);
        index += 1;
      }
    }
    const groupId = group !== null && !used.has(group) ? group : rule.id(lead);
    used.add(groupId);
    runs.push({ groupId, items: run });
  }
  return runs;
}
